import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import { Cubit } from './core'
import { createUserProfileToken } from '../firestore/UserProfileToken'
import { createInvitationInstructorWithCatalog } from '../firestore/Invitation'
import type { StaticModelCollection } from '../types'
import { InvitationType } from '../types'
import { Catalog } from '../models/Catalog'
import { getCatalogs } from '../firestore/Catalog'
import { UserProfileRole } from '../firestore/UserProfile/types'
import { fetchOrganizationsWithAdminAndInstructorIds } from '../firestore/Organization'
import { doc, serverTimestamp } from 'firebase/firestore'
import { updateDocWithError } from '../firestore-mobx/fetch'
import { fetchUserSearch, getUserSearch } from '../firestore/UserSearch'
import type { UserSearch } from '@breakoutlearning/firebase-repository/stores/UserSearch'
import { createSectionPassRebate } from '../firestore/UserProfileSectionPass'
import { deleteTAInstructors } from '../firestore/UserProfile'

export class AdminUsersCubit extends Cubit {
  repository: FirebaseRepository

  @observable
  role: UserProfileRole = UserProfileRole.instructor

  _catalogs: StaticModelCollection<Catalog>

  searchedUsers = observable.map<UserProfileRole, UserSearch[]>()
  searchFilter = observable.map<UserProfileRole, string[]>()

  organizationsWithUserIds =
    observable.array<
      Awaited<
        ReturnType<typeof fetchOrganizationsWithAdminAndInstructorIds>
      >[number]
    >()

  @observable organizationsLoading = true
  @observable isLoading = false

  constructor(repository: FirebaseRepository) {
    super()
    makeObservable(this)

    this._catalogs = Catalog.emptyCollection(repository)
    this.repository = repository
  }

  initialize(): void {
    // this.refetchUsers()
    this.fetchOrgsWithUserIds()
  }

  get catalogs() {
    if (this.hasStream('catalogs')) {
      return this._catalogs
    }

    this.addStream(
      getCatalogs(this.repository),
      (catalogs) => {
        this._catalogs.replaceModels(
          catalogs.sort((a, b) =>
            a.data.catalogName.localeCompare(b.data.catalogName)
          )
        )
      },
      {
        name: 'catalogs',
      }
    )

    return this._catalogs
  }

  @computed
  get currentSearchFilter(): string[] {
    return this.searchFilter.get(this.role) || []
  }

  @action
  async fetchOrgsWithUserIds() {
    const orgData = await fetchOrganizationsWithAdminAndInstructorIds(
      this.repository
    )
    runInAction(() => {
      this.organizationsWithUserIds.replace(orgData)
      this.organizationsLoading = false
    })
  }

  async refetchUsers(): Promise<void> {
    this.changeLoading(true)
    const role = this.role
    const filter = this.searchFilter.get(role) || []

    if (this.searchFilterIsTooBroad) {
      console.warn(
        `Search filter is too broad for role ${role}. Not fetching users.`
      )
      return
    }

    let roleParam: string | string[] = role

    if (role === UserProfileRole.admin) {
      roleParam = [
        UserProfileRole.admin,
        UserProfileRole.corre,
        UserProfileRole.author,
        UserProfileRole.editor,
      ]
    }

    if (!filter || filter.length === 0) {
      if (role === UserProfileRole.admin) {
        try {
          const users = await fetchUserSearch(this.repository, roleParam)
          runInAction(() => {
            this.searchedUsers.set(role, users)
          })
        } catch (error) {
          console.error('Error fetching all admins:', error)
          runInAction(() => {
            this.searchedUsers.set(role, [])
          })
        }
        this.changeLoading(false)
        return
      } else {
        runInAction(() => {
          this.searchedUsers.set(role, [])
        })
        this.changeLoading(false)
        return
      }
    }

    try {
      const pillPromises = filter.map(async (pill) => {
        const words = pill.trim().split(/\s+/).filter(Boolean)

        if (words.length === 0) {
          return []
        }

        // Single-word pill – simple search.
        if (words.length === 1) {
          return fetchUserSearch(this.repository, roleParam, words[0])
        }

        // Multi-word pill – fetch for every word then intersect.
        const wordResults = await Promise.all(
          words.map((word) => fetchUserSearch(this.repository, roleParam, word))
        )

        // Intersect on user id
        const intersectionMap = new Map<string, UserSearch>()
        for (const user of wordResults[0]) {
          intersectionMap.set(user.id, user)
        }
        for (let i = 1; i < wordResults.length; i += 1) {
          const currentSet = new Set(wordResults[i].map((u) => u.id))
          for (const id of Array.from(intersectionMap.keys())) {
            if (!currentSet.has(id)) {
              intersectionMap.delete(id)
            }
          }
        }

        return Array.from(intersectionMap.values())
      })

      const pillResults = await Promise.all(pillPromises)

      // UNION across pills
      const userMap = new Map<string, UserSearch>()
      for (const results of pillResults) {
        for (const user of results) {
          userMap.set(user.id, user)
        }
      }
      const unionUsers: UserSearch[] = Array.from(userMap.values())

      runInAction(() => {
        this.searchedUsers.set(role, unionUsers)
      })
    } catch (error) {
      console.error('Error fetching users:', error)
      runInAction(() => {
        this.searchedUsers.set(role, [])
      })
    }

    this.changeLoading(false)
  }

  createSectionPassRebate({
    price,
    userId,
  }: {
    price: number
    userId: string
  }) {
    return createSectionPassRebate(this.repository, {
      userId,
      price,
    })
  }

  @computed
  get foundUsers(): UserSearch[] {
    const role = this.role
    const users = this.searchedUsers.get(role) || []

    return users
  }

  @computed
  get searchFilterIsTooBroad() {
    const filter = this.searchFilter.get(this.role) || []
    return filter.includes('gmail.com') || filter.includes('gmail')
  }

  @action
  setSearchFilter(filter: string[]) {
    this.searchFilter.set(this.role, filter)
    this.refetchUsers()
  }

  @action
  removeSearchFilter = () => {
    this.searchFilter.delete(this.role)
    this.searchedUsers.delete(this.role)
    if (this.role === UserProfileRole.admin) {
      this.refetchUsers()
    }
  }

  @action
  changeRole(role: UserProfileRole) {
    this.role = role
    if (role === UserProfileRole.admin && !this.searchedUsers.has(role)) {
      this.refetchUsers()
    }
  }

  @action
  changeLoading(loading: boolean) {
    this.isLoading = loading
  }

  grantTokens(userId: string, tokenQuantity: number) {
    return createUserProfileToken(this.repository, {
      tokenQuantity: tokenQuantity,
      userId: userId,
    })
  }

  changeUserRole = async (
    userId: string,
    role: UserProfileRole,
    options?: {
      waitForSearchUserUpdate?: boolean
      deleteTAInstructors?: boolean
    }
  ) => {
    // Cannot change your own role.
    if (userId === this.repository.uid)
      throw new Error('Cannot change your own role')

    const ref = doc(this.repository.firestore, 'user_profile', userId)

    await updateDocWithError(
      ref,
      { role, updatedAt: serverTimestamp() },
      'ChangeRoleError'
    )

    // when changing from TA to another role, delete records in instructor sub-collection
    if (this.role === UserProfileRole.ta)
      await deleteTAInstructors(this.repository, userId)

    if (!options?.waitForSearchUserUpdate) return

    // Wait for the `search_user` doc to reflect the updated role.
    // this fires on a trigger after the `user_profile` doc is updated,
    await new Promise<void>((_resolve, _reject) => {
      try {
        // eslint-disable-next-line prefer-const
        let timeoutId: NodeJS.Timeout | undefined
        // eslint-disable-next-line prefer-const
        let cancelListen: () => void | undefined

        const settle = (error?: Error) => {
          clearTimeout(timeoutId)
          cancelListen?.()
          if (error) return _reject(error)
          _resolve()
        }

        const streamActions = getUserSearch(this.repository, userId).listen(
          (user) => {
            if (user?.data.role === role) settle()
          },
          {
            onError: (error) => settle(error),
          }
        )

        cancelListen = () => streamActions.cancel()
        timeoutId = setTimeout(() => {
          settle(new Error('Timed out waiting for search_user role to update'))
        }, 5000)
      } catch (error) {
        _reject(error)
      }
    })
  }

  // Future<bool?> grantToken(String userId, int tokenQuantity) async {
  //   await createUserProfileToken(
  //     _firebaseRepository,
  //     tokenQuantity: tokenQuantity,
  //     userId: userId,
  //   );
  //   return true;
  // }

  async createInvitation({
    type,
    catalogId,
  }: {
    type: InvitationType
    catalogId: string
  }) {
    const doc = await createInvitationInstructorWithCatalog(this.repository, {
      catalogId: catalogId,
      oneTime: type === InvitationType.oneTime,
    })

    return doc.id
  }
}
